import React from "react";
import { Checkbox } from "@/components/ui/checkbox";
import { TableCell, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { CurrencyAmount } from "@/components/ui/CurrencyAmount";
import { ASSET_CLASS_ICONS } from "@/constants/investmentIcons";
import { AssetsTableRowActions } from "./AssetsTableRowActions";
import { ProvenanceBadge } from "./ProvenanceBadge";
import type { UnifiedAsset } from "@/hooks/useAssetsUnified";

interface AssetsTableRowProps {
  asset: UnifiedAsset;
  isSelected: boolean;
  onSelectAsset: (assetId: string, checked: boolean) => void;
  onRowClick: (asset: UnifiedAsset) => void;
  onViewDetails: (asset: UnifiedAsset) => void;
  onEdit: (asset: UnifiedAsset) => void;
  onDelete?: (asset: UnifiedAsset) => void;
  getAssetTypeColor: (assetType: string) => string;
  onOpenAssociatedAccount: (asset: UnifiedAsset) => void;
}

export const AssetsTableRow: React.FC<AssetsTableRowProps> = ({
  asset,
  isSelected,
  onSelectAsset,
  onRowClick,
  onViewDetails,
  onEdit,
  onDelete,
  getAssetTypeColor,
  onOpenAssociatedAccount
}) => {
  const AssetIcon = ASSET_CLASS_ICONS[asset.asset_type] || ASSET_CLASS_ICONS['unclassified'];

  return (
    <TableRow 
      key={asset.id}
      className="cursor-pointer border-b-0 hover:bg-muted/10 bg-white even:bg-gray-50"
      onClick={() => onRowClick(asset)}
    >
      <TableCell className="w-10 pl-2 pr-1 py-2 sm:w-12 sm:pl-3 sm:pr-2" onClick={(e) => e.stopPropagation()}>
        <Checkbox
          className="h-3.5 w-3.5"
          checked={isSelected}
          onCheckedChange={(checked) => onSelectAsset(asset.id, checked as boolean)}
          aria-label={`Select ${asset.asset_name}`}
        />
      </TableCell>
      <TableCell className="px-2 py-2">
        <div className="flex items-center gap-3">
          <AssetIcon className="h-4 w-4 text-muted-foreground shrink-0" />
          <div className="min-w-0">
            <div className="font-medium truncate">{asset.asset_name}</div>
            {asset.symbol && (
              <div className="text-xs text-muted-foreground truncate">
                {asset.symbol}
              </div>
            )}
          </div>
        </div>
      </TableCell>
      {/* Source (provenance) */}
      <TableCell className="w-12 px-2 py-2 text-center">
        {asset.source_type && (
          <ProvenanceBadge source={asset.source_type} />
        )}
      </TableCell>
      {/* Account column: only for holdings (connected). Click to open associated account drawer */}
      <TableCell className="px-3 py-2" onClick={(e) => e.stopPropagation()}>
        {asset.source_type === 'connected' ? (
          <Button variant="link" className="h-auto p-0 text-sm" onClick={() => onOpenAssociatedAccount(asset)}>
            Account
          </Button>
        ) : (
          <span className="text-sm text-muted-foreground">—</span>
        )}
      </TableCell>
      {/* Currency */}
      <TableCell className="w-16 px-2 py-2 text-center font-mono text-xs">
        {(asset.currency || 'CAD').toUpperCase()}
      </TableCell>
      {/* Value (with currency symbol, no color rules) */}
      <TableCell className="px-4 py-2 text-right">
        <CurrencyAmount
          value={asset.market_value}
          currency={asset.currency}
          showCurrency={true}
          className={"font-normal text-xs"}
        />
      </TableCell>
      <TableCell className="w-12 px-2 py-2 text-right">
        <AssetsTableRowActions
          asset={asset}
          onViewDetails={onViewDetails}
          onEdit={onEdit}
          onDelete={onDelete}
        />
      </TableCell>
    </TableRow>
  );
};
